import { get, onValue, ref } from "firebase/database";
import { db } from "../firebaseConfig";

export const WAYPOINTS_CACHE_KEY = "trekkin_waypoints_cache_v1";
export const WAYPOINTS_CACHE_META_KEY = "trekkin_waypoints_cache_meta_v1";
const WAYPOINTS_FETCH_TTL_MS = 10 * 60 * 1000;
const WAYPOINTS_IDB_DB = "trekkin-offline-v1";
const WAYPOINTS_IDB_STORE = "kv";
const WAYPOINTS_IDB_KEY = WAYPOINTS_CACHE_KEY;
const WAYPOINTS_LOCALSTORAGE_MAX_BYTES = 220_000;

function isAndroidWebViewRuntime() {
  try {
    if (typeof navigator === "undefined") return false;
    const ua = String(navigator.userAgent || "");
    return /Android/i.test(ua) && /wv|Version\/4\.0|Chrome\//i.test(ua);
  } catch {
    return false;
  }
}

function canUseIndexedDb() {
  return typeof indexedDB !== "undefined";
}

function openIdb() {
  return new Promise((resolve, reject) => {
    if (!canUseIndexedDb()) {
      resolve(null);
      return;
    }
    const req = indexedDB.open(WAYPOINTS_IDB_DB, 1);
    req.onupgradeneeded = () => {
      const dbi = req.result;
      if (!dbi.objectStoreNames.contains(WAYPOINTS_IDB_STORE)) {
        dbi.createObjectStore(WAYPOINTS_IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("idb_open_failed"));
  });
}

async function idbGetCachePayload() {
  if (!canUseIndexedDb()) return null;
  try {
    const dbi = await openIdb();
    if (!dbi) return null;
    const value = await new Promise((resolve, reject) => {
      const tx = dbi.transaction(WAYPOINTS_IDB_STORE, "readonly");
      const store = tx.objectStore(WAYPOINTS_IDB_STORE);
      const req = store.get(WAYPOINTS_IDB_KEY);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error || new Error("idb_get_failed"));
    });
    dbi.close();
    return value && typeof value === "object" ? value : null;
  } catch {
    return null;
  }
}

async function idbSetCachePayload(payload) {
  if (!canUseIndexedDb()) return;
  try {
    const dbi = await openIdb();
    if (!dbi) return;
    await new Promise((resolve, reject) => {
      const tx = dbi.transaction(WAYPOINTS_IDB_STORE, "readwrite");
      const store = tx.objectStore(WAYPOINTS_IDB_STORE);
      const req = store.put(payload, WAYPOINTS_IDB_KEY);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error || new Error("idb_put_failed"));
    });
    dbi.close();
  } catch {
    // ignore
  }
}

async function idbClearCachePayload() {
  if (!canUseIndexedDb()) return;
  try {
    const dbi = await openIdb();
    if (!dbi) return;
    await new Promise((resolve, reject) => {
      const tx = dbi.transaction(WAYPOINTS_IDB_STORE, "readwrite");
      const store = tx.objectStore(WAYPOINTS_IDB_STORE);
      const req = store.delete(WAYPOINTS_IDB_KEY);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error || new Error("idb_delete_failed"));
    });
    dbi.close();
  } catch {
    // ignore
  }
}

function writeWaypointCacheMeta(payload) {
  try {
    localStorage.setItem(
      WAYPOINTS_CACHE_META_KEY,
      JSON.stringify({
        ts: Number(payload?.ts || Date.now()),
        count: Array.isArray(payload?.list) ? payload.list.length : 0,
        idb: true,
      })
    );
  } catch {
    // ignore
  }
}

function writeWaypointLocalMirror(payload) {
  try {
    if (isAndroidWebViewRuntime()) {
      localStorage.removeItem(WAYPOINTS_CACHE_KEY);
      writeWaypointCacheMeta(payload);
      return;
    }
    const raw = JSON.stringify(payload);
    if (raw.length > WAYPOINTS_LOCALSTORAGE_MAX_BYTES) {
      localStorage.removeItem(WAYPOINTS_CACHE_KEY);
      writeWaypointCacheMeta(payload);
      return;
    }
    localStorage.setItem(WAYPOINTS_CACHE_KEY, raw);
    writeWaypointCacheMeta(payload);
  } catch {
    writeWaypointCacheMeta(payload);
  }
}

export function loadCachedWaypoints() {
  try {
    const raw = localStorage.getItem(WAYPOINTS_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.list) ? parsed.list : [];
  } catch {
    return [];
  }
}

export async function loadCachedWaypointsAsync() {
  const localList = loadCachedWaypoints();
  if (localList.length) return localList;

  const payload = await idbGetCachePayload();
  const list = Array.isArray(payload?.list) ? payload.list : [];
  if (list.length) {
    currentWaypointsList = list;
    lastWaypointsFetchTs = Number(payload?.ts || Date.now());
    writeWaypointLocalMirror(payload);
    dispatchCacheUpdate(list, lastWaypointsFetchTs);
  }
  return list;
}

export function saveCachedWaypoints(list) {
  if (!Array.isArray(list)) return null;
  try {
    const payload = { ts: Date.now(), list };
    idbSetCachePayload(payload);
    writeWaypointLocalMirror(payload);
    currentWaypointsList = list;
    lastWaypointsFetchTs = Number(payload.ts || Date.now());
    dispatchCacheUpdate(list, payload.ts);
    return payload.ts;
  } catch {
    return null;
  }
}

export function peekWaypointsCache() {
  try {
    const raw = localStorage.getItem(WAYPOINTS_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function normalizeWaypointList(raw = {}) {
  if (!raw || typeof raw !== "object") return [];
  const list = Object.entries(raw).map(([id, data]) => ({
    id,
    ...(data || {}),
  }));
  list.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  return list;
}

function dispatchCacheUpdate(list, ts) {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(
      new CustomEvent("trekkin:waypoints-cache-updated", {
        detail: { list, ts },
      })
    );
  } catch {
    // ignore
  }
}

let currentWaypointsList = [];
let lastWaypointsFetchTs = 0;
let waypointsFetchPromise = null;

export function startWaypointsIncrementalSync() {
  return fetchAndCacheWaypoints();
}

export function stopWaypointsIncrementalSync() {
  waypointsFetchPromise = null;
}

export function subscribeToWaypointsChanges(onError) {
  return onValue(
    ref(db, "waypoints"),
    (snap) => {
      const fresh = normalizeWaypointList(snap?.val() || {});
      saveCachedWaypoints(fresh);
    },
    (err) => onError?.(err)
  );
}

export async function fetchAndCacheWaypoints(options = {}) {
  const force = options?.force === true;
  let cachedPayload = peekWaypointsCache();
  if (!cachedPayload) {
    cachedPayload = await idbGetCachePayload();
    if (cachedPayload?.list) {
      writeWaypointLocalMirror(cachedPayload);
    }
  }
  const cachedList = Array.isArray(cachedPayload?.list) ? cachedPayload.list : [];
  const cachedTs = Number(cachedPayload?.ts || 0);
  const now = Date.now();

  if (
    !force &&
    Array.isArray(currentWaypointsList) &&
    currentWaypointsList.length &&
    now - Number(lastWaypointsFetchTs || 0) < WAYPOINTS_FETCH_TTL_MS
  ) {
    return {
      list: currentWaypointsList,
      ts: Number(lastWaypointsFetchTs || cachedTs || now),
      fromCache: true,
    };
  }

  if (!force && cachedList.length && now - cachedTs < WAYPOINTS_FETCH_TTL_MS) {
    currentWaypointsList = cachedList;
    lastWaypointsFetchTs = Number(cachedTs || now);
    return { list: cachedList, ts: cachedTs, fromCache: true };
  }

  if (waypointsFetchPromise) return waypointsFetchPromise;

  waypointsFetchPromise = get(ref(db, "waypoints"))
    .then((snap) => {
      const raw = snap?.val() || {};
      const fresh = normalizeWaypointList(raw);
      currentWaypointsList = fresh;
      const savedTs = saveCachedWaypoints(fresh);
      const ts = Number(savedTs || Date.now());
      lastWaypointsFetchTs = ts;
      dispatchCacheUpdate(fresh, ts);
      return { list: fresh, ts, fromCache: false };
    })
    .catch((err) => {
      if (cachedList.length) {
        currentWaypointsList = cachedList;
        return {
          list: cachedList,
          ts: cachedTs || Date.now(),
          fromCache: true,
          error: err,
        };
      }
      return { list: [], ts: Date.now(), fromCache: false, error: err };
    })
    .finally(() => {
      waypointsFetchPromise = null;
    });

  return waypointsFetchPromise;
}

export function clearCachedWaypoints() {
  try {
    localStorage.removeItem(WAYPOINTS_CACHE_KEY);
    localStorage.removeItem(WAYPOINTS_CACHE_META_KEY);
  } catch {}
  idbClearCachePayload();
  currentWaypointsList = [];
  lastWaypointsFetchTs = 0;
  dispatchCacheUpdate([], Date.now());
}
